import React from 'react'
import { array, bool, func, string } from 'prop-types'
import { isEmpty } from 'lodash'
import intl from 'react-intl-universal'
import classnames from 'classnames'

import { Container, Row } from 'components/qstrap'
import Button from 'components/atoms/Button'
import ProfessionSalaryItem from 'components/atoms/ProfessionSalaryItem'
import ThumbnailEmpty from 'components/molecules/ThumbnailEmpty'
import ThumbnailLoader from 'components/molecules/ThumbnailLoader'
import CampusPerfectCard from 'components/organisms/CampusPerfectCard'

const Thumbnails = ({
  data,
  kind,
  loadMoreText,
  loadMoreFunction,
  isLoading,
  isLastPage,
  isLikeable,
  openNewTab,
  noBackground,
  customFooterContent: CustomFooterContent,
}) => {
  const getPropsObj = () => {
    switch (kind) {
      case 'schools':
        return data.map((school, idx) => ({
          dataTestId: `dir-campus-card-${idx}`,
          id: school.id,
          category: intl.get(`search.sector.${school.sector}`),
          description: school.description,
          href: `/directory/${school.slug}`,
          kind: 'schools',
          likeable: isLikeable,
          likeStatus: school.liked,
          likeTarget: school.id,
          logo: school.logo,
          openNewTab,
          title: school.name,
          thumbnail: school.image,
          withFooter: true,
          className: school.enabled !== undefined && !school.enabled ? 'disabled' : '',
          footerContent: CustomFooterContent && <CustomFooterContent {...school} />,
        }))
      case 'studyFields':
        return data.map((studyField, idx) => ({
          dataTestId: `dir-major-card-${idx}`,
          id: studyField.id,
          category: studyField.parentName,
          description: studyField.description,
          href: studyField.path,
          kind: 'studyFields',
          likeable: isLikeable,
          likeStatus: studyField.liked,
          likeTarget: studyField.id,
          icon: studyField.iconName,
          openNewTab,
          title: studyField.name,
          thumbnail: studyField.thumbnail,
          withFooter: true,
          footerContent: CustomFooterContent && <CustomFooterContent {...studyField} />,
        }))
      case 'scholarships':
      case 'scholarships-directory':
        return data.map((scholarship, idx) => {
          const providers = scholarship.providers.length > 0 &&
          `Beasiswa ${scholarship.providers.map(provider => intl.get(`search.provider.${provider}`)).join(' · ')}`
          return ({
            dataTestId: `dir-scholarship-card-${idx}`,
            id: scholarship.id,
            category: providers,
            description: scholarship.description,
            href: `/scholarships/${scholarship.slug}`,
            kind: 'scholarships',
            likeable: isLikeable,
            likeStatus: scholarship.liked,
            likeTarget: scholarship.id,
            logo: scholarship.logo,
            title: scholarship.name,
            thumbnail: scholarship.thumbnail,
            withFooter: true,
            className: scholarship.status !== undefined && scholarship.status !== 'active' ? 'disabled' : '',
            grayscale: scholarship.status !== undefined && scholarship.status !== 'active',
            thumbnailOverlay: scholarship.status !== undefined && scholarship.status !== 'active' ? 'closed' : '',
          })
        })
      default:
        return data.map((profession, idx) => ({
          dataTestId: `dir-career-card-${idx}`,
          id: profession.id,
          category: profession.fieldName,
          description: profession.description,
          href: profession.path,
          kind: 'professions',
          likeable: isLikeable,
          likeStatus: profession.liked,
          likeTarget: profession.id,
          icon: profession.iconName,
          openNewTab,
          title: profession.name,
          thumbnail: profession.thumbnail,
          withFooter: true,
          footerContent: !!(profession.min_salary || profession.max_salary) &&
            <ProfessionSalaryItem
              minSalary={profession.min_salary}
              maxSalary={profession.max_salary}
            />,
        }))
    }
  }

  const classNameVal = classnames('Thumbnails p-t-md', {
    'bg-white': !noBackground,
  })

  return (
    <div className={classNameVal}>
      <div className="thumbnail-flex-container">
        <Container>
          {
            (!isLoading && isEmpty(data)) &&
            <ThumbnailEmpty kind={kind} className="empty-data"/>
          }

          <Row>
            <div className="thumbnail-flex-list">
              {
                !isEmpty(data) &&
                getPropsObj().map(datum => (
                  <div key={datum.id} className="col-lg-4 col-sm-6 m-y-sm">
                    <CampusPerfectCard {...datum} />
                  </div>
                ))
              }
            </div>
          </Row>

          {
            !isLastPage && !isLoading && (
              <Row>
                <div className="Thumbnails__load-more text-center">
                  <Button
                    href="javascript:void(0);"
                    variant="link"
                    className="button-link-load-more"
                    onClick={loadMoreFunction}
                  >
                    {loadMoreText}
                  </Button>
                </div>
              </Row>
            )
          }

          { isLoading && <ThumbnailLoader /> }
        </Container>
      </div>
      <style jsx>
        {`
          .Thumbnails {
            .thumbnail-flex-container {
              padding-top: 1rem;
              padding-bottom: 1rem;

              .thumbnail-flex-list {
                display: flex;
                flex-wrap: wrap;
              }
            }
            &__load-more {
              padding: 16px;
            }
          }
        `}
      </style>
    </div>
  )
}

Thumbnails.propTypes = {
  data: array,
  kind: string,
  loadMoreText: string,
  loadMoreFunction: func,
  isLoading: bool,
  isLastPage: bool,
  isLikeable: bool,
  customFooterContent: func,
  openNewTab: bool,
  noBackground: bool,
}

Thumbnails.defaultProps = {
  data: [],
  kind: 'schools',
  loadMoreFunction: () => {},
  isLoading: false,
  isLastPage: true,
  isLikeable: true,
  noBackground: false,
}

export default Thumbnails
